import { css } from "styled-components";

// The clicker's theme, shared by every page that paints a surface.
//
// This lives outside controller.tsx because the theme is a property of the USER,
// not of one page: a host who set dark mode on the controller and then lands on
// /upgrade/success after paying should not be flashbanged by a white page. Any
// page rendering its own idea of the theme is a bug waiting to happen, so the
// key, the resolution rule, and the tokens all have exactly one definition here.

// localStorage key holding the user's explicit choice (written by the
// controller's theme toggle).
export const THEME_KEY = "theme";

export type ThemeName = "light" | "dark";

// The user's theme: their explicit choice if they made one, else the OS
// preference. Safe to call during a useState initializer.
export const readStoredTheme = (): ThemeName => {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(THEME_KEY);
  if (stored === "dark" || stored === "light") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

// The token block every styled component reads via `var(--token)`. Emit it onto
// a page-level element and children inherit it with no plumbing — switching
// themes stays a single prop flip, no ThemeProvider or context.
export const themeTokens = (dark: boolean) => css`
  --bg-page: ${dark ? "#0b1220" : "rgb(244, 244, 246)"};
  --bg-card: ${dark ? "#1e293b" : "#fff"};
  --bg-chip: ${dark ? "#1e293b" : "#fff"};
  --bg-chip-hover: ${dark ? "#334155" : "#f3f4f6"};
  --bg-fallback: ${dark ? "#0f172a" : "#f9fafb"};
  --border-soft: ${dark ? "rgba(255, 255, 255, 0.06)" : "rgba(64, 87, 109, 0.07)"};
  --border-chip: ${dark ? "rgba(255, 255, 255, 0.14)" : "rgba(64, 87, 109, 0.18)"};
  --text-primary: ${dark ? "#e2e8f0" : "#24292f"};
  --text-secondary: ${dark ? "#cbd5e1" : "#4b5563"};
  --text-muted: ${dark ? "#94a3b8" : "#6b7280"};
  --text-fade: ${dark ? "#64748b" : "#9ca3af"};
  --text-headline: ${dark ? "#f1f5f9" : "#374151"};
  --shadow-near: ${dark ? "rgba(0, 0, 0, 0.3)" : "rgba(15, 23, 42, 0.05)"};
  --shadow-far: ${dark ? "rgba(0, 0, 0, 0.6)" : "rgba(15, 23, 42, 0.22)"};
  --link: ${dark ? "#818cf8" : "#6366f1"};
  --link-hover: ${dark ? "#a5b4fc" : "#4f46e5"};
  --font-controls-bg: ${dark ? "rgba(30, 41, 59, 0.9)" : "rgba(255, 255, 255, 0.9)"};
  /* NavButton consumes these (defined in nav-button.tsx). */
  --nav-bg: ${dark ? "rgba(255, 255, 255, 0.06)" : "rgba(64, 87, 109, 0.07)"};
  --nav-bg-hover: ${dark ? "rgba(255, 255, 255, 0.12)" : "rgba(64, 87, 109, 0.15)"};
  --nav-text: ${dark ? "rgba(226, 232, 240, 0.9)" : "rgba(36, 49, 61, 0.7)"};
`;

// The host controller's dark mode: a neutral OFF-BLACK surface, distinct from
// the copresenter remote's dark navy — the host is a different, more "cockpit"
// context and reads as its own mode. Emit AFTER themeTokens so these win. Light
// mode is shared, so this is empty there. Anything the host pays from (the
// controller's Pro overlay, the /upgrade page emails link to) wears it too.
export const hostThemeTokens = (dark: boolean) =>
  dark
    ? css`
        --bg-page: #0e0e11;
        --bg-card: #26272b;
        --bg-chip: #313237;
        --bg-chip-hover: #3b3c42;
        --bg-fallback: #1a1b1e;
        --border-soft: rgba(255, 255, 255, 0.08);
        --border-chip: rgba(255, 255, 255, 0.16);
        --text-primary: #e8e8ea;
        --text-secondary: #c3c4c9;
        --text-muted: #9c9da4;
        --text-fade: #6f7078;
        --text-headline: #f3f3f5;
        --shadow-near: rgba(0, 0, 0, 0.45);
        --shadow-far: rgba(0, 0, 0, 0.7);
        --nav-bg: rgba(255, 255, 255, 0.06);
        --nav-bg-hover: rgba(255, 255, 255, 0.12);
        --nav-text: rgba(232, 232, 234, 0.9);
        --font-controls-bg: rgba(38, 39, 43, 0.9);
      `
    : css``;
